import React, { useState } from 'react';
import './MetricCardLibrary.css';
import { 
  MdClose, 
  MdSearch, 
  MdExpandMore, 
  MdShowChart, 
  MdPieChart,
  MdBarChart,
  MdTableChart,
  MdHelpOutline
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, 
  Bar, 
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';

const cards = [
  {
    title: 'Total Observation',
    description: 'The total number of safety observations recorded.',
    category: 'SBO',
    isActive: false
  },
  {
    title: 'Total Safe Observation',
    description: 'The total number of observations marked as safe.',
    category: 'SBO',
    isActive: false
  },
  {
    title: 'People Connected',
    description: 'Number of people engaged in safety observations.',
    category: 'SBO',
    isActive: false
  },
  {
    title: 'Time Spent in SO',
    description: 'Total time dedicated to conducting safety observations.',
    category: 'SBO',
    isActive: false
  },
  {
    title: 'Total Unsafe Observation',
    description: 'The total number of observations marked as unsafe.',
    category: 'SBO',
    isActive: false
  },
  {
    title: 'Mean Time To Repair (MTTR)',
    description: 'Average time taken to repair equipment after a failure, measuring the efficiency of maintenance teams.',
    category: 'Maintenance',
    isActive: false
  },
  {
    title: 'Mean Time Between Failures (MTBF)',
    description: 'Average time between independent failures of a system, indicating the reliability of the assets.',
    category: 'Maintenance',
    isActive: false
  },
  {
    title: 'Backlog Hours',
    description: 'Total estimated hours for all pending work orders that are yet to be completed.',
    category: 'Maintenance',
    isActive: false
  },
  {
    title: 'Schedule Compliance',
    description: 'The percentage of scheduled maintenance tasks that were completed within the planned timeframe.',
    category: 'Maintenance',
    isActive: false
  },
  {
    title: 'Asset Availability',
    description: 'Percentage of time equipment is available and operational for production.',
    category: 'OEE',
    isActive: false
  },
  {
    title: 'Performance Efficiency',
    description: 'Comparison between the actual production speed and the ideal speed of the equipment.',
    category: 'OEE',
    isActive: false
  },
  {
    title: 'Quality Rate',
    description: 'Ratio of good units produced without defects compared to the total units produced.',
    category: 'OEE',
    isActive: false
  },
  {
    title: 'Overall Equipment Effectiveness (OEE)',
    description: 'Comprehensive metric combining Availability, Performance, and Quality to measure equipment productivity.',
    category: 'OEE',
    isActive: false
  },
  {
    title: 'Total Downtime',
    description: 'Sum of all time intervals where equipment was not operational due to unplanned failures or stops.',
    category: 'OEE',
    isActive: false
  },
  {
    title: 'Near Miss Incidents',
    description: 'Number of safety occurrences that did not result in injury but had the potential to do so.',
    category: 'SBO',
    isActive: false
  },
  {
    title: 'Safety Compliance Rate',
    description: 'Percentage of workers adhering to safety protocols and completing mandated safety checks.',
    category: 'SBO',
    isActive: false
  },
  {
    title: 'Total Work Orders',
    description: 'Total number of maintenance work orders created in given period.',
    category: 'Maintenance',
    isActive: false
  },
  {
    title: 'Completed Work Orders',
    description: 'Total number of work orders marked as completed.',
    category: 'Maintenance',
    isActive: false
  },
  {
    title: 'Pending Work Orders',
    description: 'Active work orders that are currently in progress or awaiting attention.',
    category: 'Maintenance',
    isActive: false
  },
  {
    title: 'Maintenance Cost',
    description: 'Total spending on maintenance activities, including parts, labor, and external services.',
    category: 'Accounting',
    isActive: false
  },
  {
    title: 'Spare Part Stock Level',
    description: 'Current inventory levels of critical spare parts compared to minimum stock requirements.',
    category: 'Accounting',
    isActive: false
  },
  {
    title: 'Vendor Response Time',
    description: 'Average time taken by external vendors to respond to service requests or deliver parts.',
    category: 'Accounting',
    isActive: false
  },
  {
    title: 'Energy Consumption',
    description: 'Total energy used by plant assets, typically measured in kWh.',
    category: 'OEE',
    isActive: false
  },
  {
    title: 'Water Consumption',
    description: 'Total water usage across the plant facilities and processes.',
    category: 'OEE',
    isActive: false
  },
  {
    title: 'Work Order Aging',
    description: 'Average number of days a work order remains open before completion.',
    category: 'Maintenance',
    isActive: false
  },
  {
    title: 'Preventive vs Corrective Maintenance Ratio',
    description: 'Comparison of planned preventive maintenance hours vs unplanned corrective maintenance hours.',
    category: 'Maintenance',
    isActive: false
  },
];

const MetricCardLibrary = ({ onClose }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('quick');
  const [activeCard, setActiveCard] = useState(cards[0]);
  const [comparison, setComparison] = useState('');
  const [chartType, setChartType] = useState('bar');

  // ... (keep previewData and COLORS) ...
  // Mock Data with comparison values
  const previewData = [
    { name: 'Dept A', value: 80, lastYear: 70, last2Year: 60 },
    { name: 'Dept B', value: 98, lastYear: 85, last2Year: 75 },
    { name: 'Dept C', value: 98, lastYear: 90, last2Year: 82 },
    { name: 'Dept D', value: 80, lastYear: 72, last2Year: 65 },
    { name: 'Dept E', value: 50, lastYear: 55, last2Year: 48 },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  const renderPreviewChart = () => {
    switch(chartType) {
      case '123':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <span style={{ fontSize: '4rem', fontWeight: 'bold', color: '#111827' }}>406</span>
            <span style={{ fontSize: '1rem', color: '#6B7280' }}>{activeCard.title}</span>
            {comparison === 'Last Year' && (
              <span style={{ fontSize: '0.875rem', color: 'var(--accent-primary)', marginTop: '8px' }}>+5% vs Last Year</span>
            )}
            {comparison === 'Last 2 Year' && (
              <span style={{ fontSize: '0.875rem', color: 'var(--accent-primary)', marginTop: '8px' }}>+12% vs 2 Years Ago</span>
            )}
          </div>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={previewData}>
              <CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="3 3" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#6B7280' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#6B7280' }} />
              <Tooltip cursor={{fill: 'transparent'}} />
              <Line type="monotone" dataKey="value" stroke="#0e5c8e" strokeWidth={3} dot={{ r: 4 }} name="Actual" />
              {(comparison === 'Last Year' || comparison === 'Last 2 Year') && (
                 <Line type="monotone" dataKey="lastYear" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" name="Last Year" />
              )}
              {comparison === 'Last 2 Year' && (
                 <Line type="monotone" dataKey="last2Year" stroke="#cbd5e1" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="3 3" name="Last 2 Year" />
              )}
            </LineChart>
          </ResponsiveContainer>
        );
      case 'pie':
        // Pie charts typically don't show comparison as multi-series, maybe just stick to Actual for now or swap data
        // For simplicity, we'll keep Pie showing Actual distribution
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={previewData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
              >
                {previewData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );
      case 'table':
        return (
          <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E5E7EB', textAlign: 'left' }}>
                  <th style={{ padding: '8px', color: '#6B7280' }}>Department</th>
                  <th style={{ padding: '8px', color: '#6B7280' }}>Value</th>
                  {(comparison === 'Last Year' || comparison === 'Last 2 Year') && (
                     <th style={{ padding: '8px', color: '#6B7280' }}>Last Year</th>
                  )}
                  {comparison === 'Last 2 Year' && (
                     <th style={{ padding: '8px', color: '#6B7280' }}>Last 2 Year</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '8px', color: '#111827' }}>{row.name}</td>
                    <td style={{ padding: '8px', fontWeight: '500', color: '#111827' }}>{row.value}</td>
                    {(comparison === 'Last Year' || comparison === 'Last 2 Year') && (
                       <td style={{ padding: '8px', color: '#6B7280' }}>{row.lastYear}</td>
                    )}
                    {comparison === 'Last 2 Year' && (
                       <td style={{ padding: '8px', color: '#9CA3AF' }}>{row.last2Year}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'bar':
      default:
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={previewData} barSize={30}>
              <CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="3 3" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#6B7280' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#6B7280' }} />
              <Tooltip cursor={{fill: 'transparent'}} />
              <Bar dataKey="value" fill="#0e5c8e" radius={[2, 2, 0, 0]} name="Actual" />
              {(comparison === 'Last Year' || comparison === 'Last 2 Year') && (
                <Bar dataKey="lastYear" fill="#94a3b8" radius={[2, 2, 0, 0]} name="Last Year" />
              )}
               {comparison === 'Last 2 Year' && (
                <Bar dataKey="last2Year" fill="#cbd5e1" radius={[2, 2, 0, 0]} name="Last 2 Year" />
              )}
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/home/bi-dashboard');
    }
  };

  const handleAdd = () => {
    const newCard = {
      id: Date.now(),
      title: activeCard.title,
      type: chartType,
      data: previewData, // Use the current mock data
      category: cards.find(c => c.title === activeCard.title)?.category || 'General'
    };

    // Save to local storage for persistence across pages
    const existingCards = JSON.parse(localStorage.getItem('dashboard_custom_cards') || '[]');
    localStorage.setItem('dashboard_custom_cards', JSON.stringify([...existingCards, newCard]));

    if (onClose) {
      onClose();
    } else {
      navigate('/home/bi-dashboard');
    }
  };

  // Search and Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Filtered Cards
  const filteredCards = cards.filter(card => {
    const matchesSearch = card.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          card.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || card.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="metric-library-page">
      <div className="metric-library-container">
        {/* Header */}
        <div className="library-header">
          <h1 className="library-title">Metric Card Library</h1>
          <div className="header-actions">
            <button className="btn-icon-action btn-close" onClick={handleClose} title="Close">
              <MdClose size={24} />
            </button>
          </div>
        </div>

        <div className="library-body">
          {/* Left Sidebar */}
          <div className="library-sidebar">
            <div className="sidebar-tabs">
              <button 
                className={`tab-btn active`}
                // onClick={() => setActiveTab('quick')} // Tab switching no longer needed
              >
                Quick Cards
              </button>
              {/* Custom Tab Removed */}
            </div>

            <div className="sidebar-search">
              <div className="search-row">
                <div className="input-group">
                  <input 
                    className="search-input" 
                    placeholder="Search..." 
                    type="text" 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <MdSearch className="input-icon" />
                </div>
                <div className="category-select-group">
                  <select 
                    className="form-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="All">All Categories</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="OEE">OEE</option>
                    <option value="SBO">SBO</option>
                    <option value="Accounting">Accounting</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="card-list custom-scrollbar">
              {filteredCards.length > 0 ? (
                filteredCards.map((card, index) => (
                  <div 
                    key={index}
                    className={`metric-card-item ${activeCard.title === card.title ? 'active' : ''}`}
                    onClick={() => setActiveCard(card)}
                  >
                    <h3 className="item-title">{card.title}</h3>
                    <p className="item-desc">{card.description}</p>
                    {card.category && (
                      <span className="category-tag">{card.category}</span>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#6B7280' }}>
                  No cards found matching your search.
                </div>
              )}
            </div>
          </div>

          {/* Right Content */}
          <div className="library-preview">
            <div className="configure-header">
              <h2 className="preview-title">Configure</h2>
            </div>
            
            <div className="preview-content">
              {/* Config Panel */}
              <div className="configure-panel custom-scrollbar">
                
                {/* Card Type */}
                <div className="form-group">
                  <label className="form-label">Card Type</label>
                  <div className="chart-type-group">
                    <button 
                      className={`btn-chart-type ${chartType === '123' ? 'active' : ''}`}
                      onClick={() => setChartType('123')}
                    >
                      <span className="chart-type-text">123</span>
                    </button>
                    <button 
                      className={`btn-chart-type ${chartType === 'bar' ? 'active' : ''}`}
                      onClick={() => setChartType('bar')}
                    >
                      <MdBarChart size={24} />
                    </button>
                    <button 
                      className={`btn-chart-type ${chartType === 'line' ? 'active' : ''}`}
                      onClick={() => setChartType('line')}
                    >
                      <MdShowChart size={24} />
                    </button>
                    <button 
                      className={`btn-chart-type ${chartType === 'pie' ? 'active' : ''}`}
                      onClick={() => setChartType('pie')}
                    >
                      <MdPieChart size={24} />
                    </button>
                     <button 
                      className={`btn-chart-type ${chartType === 'table' ? 'active' : ''}`}
                      onClick={() => setChartType('table')}
                    >
                      <MdTableChart size={24} />
                    </button>
                  </div>
                </div>

                {/* Chart Title */}
                <div className="form-group">
                  <label className="form-label">
                    Chart Title <span className="required">*</span>
                  </label>
                  <input 
                    type="text" 
                    className="form-input"
                    value={activeCard.title}
                    onChange={(e) => setActiveCard({ ...activeCard, title: e.target.value })}
                  />
                </div>

                {/* Comparison Select */}
                {/* Comparison Select */}
                <div className="form-group">
                  <label className="form-label">Comparison</label>
                  <div className="select-wrapper">
                    <select 
                      className="form-select"
                      value={comparison}
                      onChange={(e) => setComparison(e.target.value)}
                    >
                      <option value="">Select Comparison</option>
                      <option value="Last Year">Last Year</option>
                      <option value="Last 2 Year">Last 2 Year</option>
                    </select>
                    <MdExpandMore className="toggle-icon" />
                  </div>
                </div>

                {/* Description Text */}
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <p className="helper-text">
                  {activeCard.description}
                  </p>
                </div>
              </div>

              {/* Preview Pane - Charts */}
              <div className="preview-pane">
                <div className="chart-preview-card">
                  <h4 className="preview-card-title">{activeCard.title}</h4>
                  
                  <div className="chart-container">
                    {renderPreviewChart()}
                  </div>
                  
                  {chartType !== '123' && chartType !== 'table' && (
                    <div className="preview-legend">
                       <span className="legend-dot"></span> Actual
                    </div>
                  )}

                </div>
              </div>

            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="library-footer">
          <button className="btn-secondary" onClick={handleClose}>Cancel</button>
          <button className="btn-primary" onClick={handleAdd}>Add to Dashboard</button>
        </div>
      </div>

    </div>
  );
};

export default MetricCardLibrary;